import { useLayoutEffect, useRef } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Logo } from '../common/Logo'
import { blurFocusOutsideDialogs } from '../../lib/blurFocusOutsideDialogs'
import { GuidePreview } from './GuidePreviews'

/**
 * Shared dialog chrome for orientation and surface guides.
 *
 * Layout (common product-tour pattern):
 * - Header: optional logo, optional eyebrow, single headline, optional intro line, step counter
 * - Body: explanatory copy first, then optional preview (reference image)
 */
export function GuideDialogShell({
  open,
  eyebrow,
  title,
  intro,
  stepIndex = 0,
  stepCount = 1,
  onClose,
  onBack,
  onNext,
  onFinish,
  primaryLabel = 'Next',
  secondaryLabel = 'Remind me later',
  showSecondary = true,
  showBrand = true,
  previewKey,
  body,
}) {
  const primaryRef = useRef(null)
  const isLast = stepIndex >= stepCount - 1
  const hasPreview = Boolean(previewKey)

  useLayoutEffect(() => {
    if (!open) return
    blurFocusOutsideDialogs()
    const frame = window.requestAnimationFrame(() => {
      primaryRef.current?.focus({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [open, stepIndex])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      disableAutoFocus
      aria-labelledby="guide-dialog-title"
      slotProps={{
        transition: {
          onEntered: () => {
            primaryRef.current?.focus({ preventScroll: true })
          },
        },
        paper: {
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            border: (t) => `1px solid ${t.palette.divider}`,
          },
        },
      }}
    >
      <DialogTitle
        id="guide-dialog-title"
        component="div"
        sx={{ pb: 0, pt: 2.5, px: 3, pr: 6 }}
      >
        {showBrand ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Logo variant="periwinkle" height={32} />
          </Box>
        ) : null}
        {eyebrow ? (
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.4, letterSpacing: 0.6 }}
          >
            {eyebrow}
          </Typography>
        ) : null}
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, display: 'block' }}>
          {title}
        </Typography>
        {intro ? (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
            {intro}
          </Typography>
        ) : null}
        {stepCount > 1 ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Step {stepIndex + 1} of {stepCount}
          </Typography>
        ) : null}
        <IconButton
          aria-label="Close guide"
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {body ? (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              lineHeight: 1.65,
              mb: hasPreview ? 2 : 0,
            }}
          >
            {body}
          </Typography>
        ) : null}
        {hasPreview ? <GuidePreview previewKey={previewKey} /> : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: 1,
          borderTop: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        {showSecondary ? (
          <Button onClick={onClose} color="inherit" fullWidth sx={{ maxWidth: { sm: 180 } }}>
            {secondaryLabel}
          </Button>
        ) : null}
        {onBack && stepIndex > 0 ? (
          <Button onClick={onBack} color="inherit" sx={{ mr: { sm: 'auto' } }}>
            Back
          </Button>
        ) : (
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
        )}
        <Button
          ref={primaryRef}
          variant="contained"
          color="primary"
          onClick={isLast ? onFinish : onNext}
          fullWidth
          sx={{ maxWidth: { sm: 200 } }}
        >
          {isLast ? (primaryLabel === 'Next' ? 'Done' : primaryLabel) : primaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
